import React, { useState } from "react";
import Modal from "../../components/Modal";
import { Spinner } from "../../components/Loader";
import { errorMessage } from "../../services";
import { deleteSite, requestSiteDeletion, withdrawSiteDeletion } from "../../services/sites";

const when = (date) => new Date(date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

// Deleting one website. Either way the website address (slug) is typed before the button unlocks; the server checks it.
// - Super Admin: deletes it for good. When the owner asked for it, their request (who, when, why) shows, and the
//   request can be rejected instead, with a note the owner sees in their alerts.
// - Store owner / franchise partner: asks for it to be deleted, with an optional reason. Ownezzy reviews the request.
// onDone(message, deleted): deleted is true when the website is gone.
export default function DeleteSiteModal({ site, isSuper, onClose, onDone }) {
  const [typed, setTyped] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const matches = typed.trim().toLowerCase() === site.id;
  const request = site.deletionRequest;

  const run = (kind, call, deleted) => async () => {
    setBusy(kind);
    setError("");
    try {
      const { data } = await call();
      onDone(data.message, deleted);
    } catch (err) {
      setError(errorMessage(err));
      setBusy("");
    }
  };
  const remove = run("delete", () => deleteSite(site.id, typed.trim()), true);
  const ask = run("request", () => requestSiteDeletion(site.id, typed.trim(), reason.trim()), false);
  const reject = run("reject", () => withdrawSiteDeletion(site.id, note.trim()), false);
  const submit = (e) => {
    e.preventDefault();
    if (matches) (isSuper ? remove : ask)();
  };

  return (
    <Modal eyebrow={isSuper ? "DELETE WEBSITE" : "REQUEST DELETION"} title={isSuper ? `Delete ${site.name}?` : `Ask to delete ${site.name}?`} onClose={busy ? () => {} : onClose}>
      <form onSubmit={submit} className="delete-site">
        {isSuper && request && (
          <div className="deletion-request-box">
            <b>{request.by || "The owner"} asked to delete this website on {when(request.requestedAt)}.</b>
            {request.reason ? <p>Reason: {request.reason}</p> : <p className="muted">No reason given.</p>}
          </div>
        )}
        <div className="delete-site-warning">
          <span aria-hidden="true">⚠</span>
          <div>
            <b>{isSuper ? "This cannot be undone." : "Once Ownezzy approves it, this cannot be undone."}</b>
            <p>{site.domain} stops working, and these are deleted for good:</p>
            <ul>
              <li>All products, categories and images</li>
              <li>All orders and shopper accounts</li>
              <li>Banners, pages and store settings</li>
            </ul>
            <p className="muted">
              {isSuper
                ? "Subscription payments are kept in Payments. If this is the owner's only website, their account and staff logins are deleted too; otherwise their account stays."
                : "Your other websites and your account stay. If this is your only website, your account is closed too. You can cancel the request until it is approved."}
            </p>
          </div>
        </div>
        {!isSuper && (
          <label className="admin-field">Why do you want to delete it? (optional)
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} rows={3} placeholder="e.g. I made this website by mistake" />
          </label>
        )}
        {error && <div className="form-error">{error}</div>}
        <label className="admin-field">Type <code>{site.id}</code> to confirm
          <input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" autoCapitalize="none" spellCheck="false" placeholder={site.id} autoFocus />
        </label>
        <div className="product-modal-actions">
          <button type="button" className="outline-btn" onClick={onClose} disabled={Boolean(busy)}>Cancel</button>
          <button className="danger-btn" disabled={!matches || Boolean(busy)}>
            {busy === "delete" ? <><Spinner /> Deleting</> : busy === "request" ? <><Spinner /> Sending</> : isSuper ? "Delete website" : "Request deletion"}
          </button>
        </div>
        {isSuper && request && (
          <div className="deletion-reject">
            <label className="admin-field">Or keep the website and reject the request
              <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} placeholder="Note for the owner (optional)" />
            </label>
            <button type="button" className="outline-btn" onClick={reject} disabled={Boolean(busy)}>
              {busy === "reject" ? <><Spinner /> Rejecting</> : "Reject request"}
            </button>
          </div>
        )}
      </form>
    </Modal>
  );
}
